import React, { useEffect, useMemo, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { ViewportDrawer } from '../Overlay';
import type { ClxChannel, ClxDrawerPatient, PaymentLinkRow } from '../../collections/types';
import { fmtMoney } from './CollectionsView';

export interface CreateLinkDrawerProps {
  open: boolean;
  patient: ClxDrawerPatient;
  /** "Send again" prefills amount / channel / label from a dead row (§6). */
  prefill: PaymentLinkRow | null;
  onClose: () => void;
}

const PRACTICE = 'Central Park Dental';

const SEGMENTS: { id: ClxChannel; label: string }[] = [
  { id: 'text', label: 'Text' },
  { id: 'email', label: 'Email' },
  { id: 'qr', label: 'QR at the desk' },
];

const EXPIRIES: { id: '24h' | '48h' | '7d'; label: string }[] = [
  { id: '24h', label: '24 hours' },
  { id: '48h', label: '48 hours' },
  { id: '7d', label: '7 days' },
];

const SEND_BUTTON: Record<ClxChannel, string> = {
  text: 'Create and send the text',
  email: 'Create and send the email',
  qr: 'Create and show the QR',
};

function parseAmount(s: string): number {
  const n = Number(s.replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : 0;
}

export function CreateLinkDrawer({ open, patient, prefill, onClose }: CreateLinkDrawerProps) {
  const initialChannel: ClxChannel = prefill?.channel ?? (patient.optedOutText ? 'email' : 'text');
  const [channel, setChannel] = useState<ClxChannel>(initialChannel);
  const [amount, setAmount] = useState<string>(String(prefill?.amount ?? patient.balance));
  const [label, setLabel] = useState<string>(prefill?.label ?? '');
  const [expires, setExpires] = useState<'24h' | '48h' | '7d'>('24h');
  const [contactId, setContactId] = useState<string>('');
  const [manual, setManual] = useState(false);
  const [manualValue, setManualValue] = useState('');

  // Reset when the drawer (re)opens so a fresh Create or Send-again starts clean.
  useEffect(() => {
    if (!open) return;
    const ch: ClxChannel = prefill?.channel ?? (patient.optedOutText ? 'email' : 'text');
    setChannel(ch);
    setAmount(String(prefill?.amount ?? patient.balance));
    setLabel(prefill?.label ?? '');
    setExpires('24h');
    setContactId('');
    setManual(false);
    setManualValue('');
  }, [open, prefill, patient]);

  const channelContacts = channel === 'qr' ? [] : patient.contacts[channel];
  // Default-select the first enabled contact when the channel changes.
  useEffect(() => {
    if (channel === 'qr') return;
    const first = channelContacts.find((c) => !c.disabled);
    setContactId(first ? first.id : '');
    setManual(channelContacts.length === 0);
  }, [channel]); // eslint-disable-line react-hooks/exhaustive-deps

  const amountValue = parseAmount(amount);
  const aboveBalance = amountValue > patient.balance;
  const canCreate = amountValue > 0 && (channel === 'qr' || (manual ? manualValue.trim() !== '' : contactId !== ''));
  const textOptedOut = channel === 'text' && patient.optedOutText === true;

  const preview = useMemo(() => {
    const amt = amountValue > 0 ? fmtMoney(amountValue) : fmtMoney(patient.balance);
    return `${PRACTICE}: your balance of ${amt} is ready to pay. Pay securely here: elva.to/p/7Kq2`;
  }, [amountValue, patient.balance]);

  return (
    <ViewportDrawer open={open} onClose={onClose} width="min(700px, 96vw)">
      <div className="flex h-full flex-col bg-[var(--surface-card)]">
        {/* header */}
        <div className="flex items-start justify-between gap-3 border-b border-[var(--border-default)] px-5 py-4">
          <div>
            <h2 className="m-0 text-[17px] font-bold text-[var(--text-primary)]">Create a payment link</h2>
            <p className="m-0 mt-0.5 text-[12px] text-[var(--text-muted)]">One patient, one amount, one link.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--surface-sunken)] text-[var(--text-secondary)] hover:bg-[var(--surface-header)]"
          >
            <Icon kind="x" size={15} stroke={2} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-5">
          {/* §8.1 patient — picked, never typed */}
          <div className="rounded-[var(--r-lg)] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--brand-indigo)] text-[12px] font-bold text-[var(--text-on-accent)]">
                {patient.initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[14px] font-bold text-[var(--text-primary)]">{patient.name}</span>
                  <button type="button" className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline">
                    Change
                  </button>
                </div>
                <div className="text-[11.5px] text-[var(--text-muted)]">{patient.chartLine}</div>
              </div>
            </div>
            <div className="mt-3 border-t border-[var(--border-default)] pt-3">
              <div className="flex items-baseline justify-between">
                <span className="text-[12.5px] text-[var(--text-secondary)]">Balance on file</span>
                <span className="font-[var(--font-mono)] text-[15px] font-bold text-[var(--text-primary)]">
                  {fmtMoney(patient.balance)}
                </span>
              </div>
              <p className="m-0 mt-1 flex items-start gap-1.5 text-[11px] leading-[1.5] text-[var(--text-muted)]">
                <Icon kind="refresh" size={11} stroke={2} style={{ color: 'var(--text-muted)', marginTop: 2 }} />
                {patient.balanceProvenance}
              </p>
            </div>
          </div>

          {/* §8.8 the collision case — InlineHold, does not block */}
          {patient.arSequencingNote ? (
            <div className="flex items-start gap-2 rounded-[var(--r-md)] border border-[var(--warn-amber-line)] bg-[var(--warn-amber-fill)] px-3 py-2.5">
              <Icon kind="alert" size={14} stroke={2} style={{ color: 'var(--warn-amber)', marginTop: 1 }} />
              <p className="m-0 text-[12px] leading-[1.5] text-[var(--text-secondary)]">{patient.arSequencingNote}</p>
            </div>
          ) : null}

          {/* §8.2 amount */}
          <Field label="Amount">
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 font-[var(--font-mono)] text-[20px] font-bold text-[var(--text-muted)]">
                $
              </span>
              <input
                value={amount}
                onChange={(e) => { setAmount(e.target.value); }}
                inputMode="decimal"
                className="w-full rounded-[var(--r-md)] border border-[var(--border-default)] bg-[var(--surface-card)] py-2.5 pl-8 pr-28 font-[var(--font-mono)] text-[20px] font-bold text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
              />
              <button
                type="button"
                onClick={() => { setAmount(String(patient.balance)); }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full border border-[var(--border-default)] bg-[var(--surface-sunken)] px-2.5 py-1 text-[11px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-header)]"
              >
                Use the balance
              </button>
            </div>
            <p className="m-0 mt-1.5 text-[11.5px] leading-[1.5] text-[var(--text-muted)]">
              Prefilled from the balance. Type over it for a part payment — anything <strong>above</strong> the balance
              asks you to confirm.
            </p>
            {aboveBalance ? (
              <div className="mt-2 flex items-start gap-2 rounded-[var(--r-md)] border border-[var(--warn-amber-line)] bg-[var(--warn-amber-fill)] px-3 py-2 text-[12px] leading-[1.5] text-[var(--text-secondary)]">
                <Icon kind="alert" size={13} stroke={2} style={{ color: 'var(--warn-amber)', marginTop: 1 }} />
                <span>
                  That is <strong>{fmtMoney(amountValue)}</strong> against a balance of{' '}
                  <strong>{fmtMoney(patient.balance)}</strong>. Sending more than the balance takes a deposit — confirm it
                  is what you meant.
                </span>
              </div>
            ) : null}
          </Field>

          {/* §8.3 how to send — segmented control */}
          <Field label="How to send">
            <div className="inline-flex rounded-[var(--r-md)] border border-[var(--border-default)] bg-[var(--surface-sunken)] p-0.5">
              {SEGMENTS.map((s) => {
                const isOff = s.id === 'text' && patient.optedOutText === true;
                return (
                  <button
                    key={s.id}
                    type="button"
                    disabled={isOff}
                    onClick={() => { setChannel(s.id); }}
                    className={cn(
                      'rounded-[calc(var(--r-md)-2px)] px-3.5 py-1.5 text-[12.5px] font-semibold transition',
                      channel === s.id
                        ? 'bg-[var(--surface-card)] text-[var(--text-primary)] shadow-[var(--shadow-card)]'
                        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
                      isOff && 'cursor-not-allowed opacity-40',
                    )}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>

            {textOptedOut ? (
              <p className="m-0 mt-2 text-[12px] text-[var(--warn-amber)]">
                {patient.name.split(' ')[0]} has opted out of texts. Email and QR stay available.
              </p>
            ) : null}

            {channel === 'qr' ? (
              <p className="m-0 mt-3 flex items-start gap-2 rounded-[var(--r-md)] border border-[var(--border-default)] bg-[var(--surface-sunken)] px-3 py-2.5 text-[12px] leading-[1.5] text-[var(--text-secondary)]">
                <Icon kind="grid" size={14} stroke={2} style={{ color: 'var(--text-muted)', marginTop: 1 }} />
                A QR code is shown at the desk after you create it. The row lands in the list as{' '}
                <em>Waiting</em>, marked <em>Shown at the front desk</em>.
              </p>
            ) : (
              <div className="mt-3 space-y-1.5">
                {!manual ? (
                  <>
                    {channelContacts.map((c) => (
                      <label
                        key={c.id}
                        className={cn(
                          'flex cursor-pointer items-start gap-2.5 rounded-[var(--r-md)] border px-3 py-2',
                          contactId === c.id
                            ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo-tint)]'
                            : 'border-[var(--border-default)] bg-[var(--surface-card)]',
                          c.disabled && 'cursor-not-allowed opacity-55',
                        )}
                      >
                        <input
                          type="radio"
                          name="clx-contact"
                          disabled={c.disabled}
                          checked={contactId === c.id}
                          onChange={() => { setContactId(c.id); }}
                          className="mt-0.5 accent-[var(--brand-indigo)]"
                        />
                        <span className="min-w-0">
                          <span className="block text-[13px] font-semibold text-[var(--text-primary)]">{c.value}</span>
                          <span className="block text-[11.5px] text-[var(--text-muted)]">{c.meta}</span>
                        </span>
                      </label>
                    ))}
                    <button
                      type="button"
                      onClick={() => { setManual(true); }}
                      className="text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline"
                    >
                      Send to a number that is not on file →
                    </button>
                  </>
                ) : (
                  <div className="rounded-[var(--r-md)] border border-[var(--warn-amber-line)] bg-[var(--warn-amber-fill)] p-3">
                    <div className="mb-1 flex items-center gap-1.5 text-[11.5px] font-semibold text-[var(--warn-amber)]">
                      <Icon kind="shield" size={12} stroke={2} />
                      Not on file — recorded against your name, not written back to the chart.
                    </div>
                    <input
                      value={manualValue}
                      onChange={(e) => { setManualValue(e.target.value); }}
                      placeholder={channel === 'email' ? 'name@example.com' : '(415) 555‑0000'}
                      className="w-full rounded-[var(--r-md)] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-2 text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
                    />
                    {channelContacts.length > 0 ? (
                      <button
                        type="button"
                        onClick={() => { setManual(false); }}
                        className="mt-1.5 text-[12px] font-semibold text-[var(--brand-indigo)] hover:underline"
                      >
                        ← Back to contacts on file
                      </button>
                    ) : (
                      <p className="m-0 mt-1.5 text-[11.5px] text-[var(--text-muted)]">
                        {patient.name.split(' ')[0]} has no {channel === 'email' ? 'email' : 'mobile number'} on file.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </Field>

          {/* §8.4 what the patient gets — show the message */}
          <Field label="What the patient gets">
            <div className="rounded-[var(--r-lg)] rounded-bl-[4px] bg-[var(--brand-indigo-tint)] px-3.5 py-2.5 text-[12.5px] leading-[1.55] text-[var(--text-primary)]">
              {preview}
              <div className="mt-1 text-[var(--text-muted)]">Reply STOP to opt out.</div>
            </div>
            <p className="m-0 mt-2 flex items-start gap-1.5 text-[11px] leading-[1.5] text-[var(--text-muted)]">
              <Icon kind="shield-check" size={12} stroke={2} style={{ color: 'var(--money-green)', marginTop: 1 }} />
              Fixed wording. <strong className="mx-1 font-semibold text-[var(--text-secondary)]">No procedure name and no clinical detail</strong>
              — and your label below never appears in it. The pay page shows the amount and the practice name, nothing
              else.
            </p>
          </Field>

          {/* §8.5 label — internal only */}
          <Field label="Label (optional)">
            <input
              value={label}
              onChange={(e) => { setLabel(e.target.value); }}
              placeholder="e.g. Crown balance"
              className="w-full rounded-[var(--r-md)] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-2 text-[13px] text-[var(--text-primary)] outline-none focus:border-[var(--brand-indigo)]"
            />
            <p className="m-0 mt-1.5 text-[11.5px] leading-[1.5] text-[var(--text-muted)]">
              Only your team sees this — it is how you find the row in the list later. It is not sent to the patient.
            </p>
          </Field>

          {/* §8.6 expires in */}
          <Field label="Expires in">
            <div className="flex flex-wrap items-center gap-2">
              {EXPIRIES.map((x) => (
                <button
                  key={x.id}
                  type="button"
                  onClick={() => { setExpires(x.id); }}
                  className={cn(
                    'rounded-full border px-3 py-1 text-[12.5px] font-semibold',
                    expires === x.id
                      ? 'border-[var(--brand-indigo)] bg-[var(--brand-indigo)] text-[var(--text-on-accent)]'
                      : 'border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]',
                  )}
                >
                  {x.label}
                </button>
              ))}
            </div>
            <p className="m-0 mt-1.5 text-[11.5px] leading-[1.5] text-[var(--text-muted)]">
              If it expires unpaid, <strong>Send again</strong> on the row issues a fresh link. Nobody has to rebuild it.
            </p>
          </Field>
        </div>

        {/* §8.7 footer */}
        <div className="flex items-center justify-between gap-3 border-t border-[var(--border-default)] px-5 py-3.5">
          <span className="text-[11.5px] text-[var(--text-muted)]">Sends as soon as you press it.</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[var(--r-md)] px-3.5 py-2 text-[13px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)]"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!canCreate}
              onClick={onClose}
              className="inline-flex items-center gap-1.5 rounded-[var(--r-md)] bg-[var(--brand-indigo)] px-4 py-2 text-[13px] font-semibold text-[var(--text-on-accent)] disabled:opacity-40"
            >
              <Icon kind={channel === 'qr' ? 'grid' : 'send'} size={14} stroke={2} color="#fff" />
              {SEND_BUTTON[channel]}
            </button>
          </div>
        </div>
      </div>
    </ViewportDrawer>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">
        {label}
      </div>
      {children}
    </div>
  );
}
